'use client'

import { ButtonLink, KycAlert } from '../'
import { useTranslate, useLanguage } from '../../hooks'
import {
  useUserAuthDetails,
  useUserPortfolio,
  useGetFiatTransactionHistory,
} from '@repo/services'
import { Card, Col, Icon, Row, Typography } from '@repo/ui/components'
import { useIsMobile } from '@repo/ui/hooks'
import { cn } from '@repo/ui/lib'
import Image from 'next/image'
import { useMemo } from 'react'

interface WalletProgressAlertProps {
  className?: string
}

export const WalletProgressAlert = (props: WalletProgressAlertProps) => {
  const { className } = props
  const { t } = useTranslate()
  const isMobile = useIsMobile()
  const language = useLanguage()

  const { data: authDetails, isPending: isPendingAuth } = useUserAuthDetails()

  const {
    data: { data: { accounts = [] } = {} } = {},
    isPending: isPendingPortfolio,
  } = useUserPortfolio()

  const { data: transactionData, isPending: isPendingTransactions } =
    useGetFiatTransactionHistory({
      skip: 0,
      limit: 1,
    })

  // Check KYC status
  const hasKyc = useMemo(() => {
    return (
      authDetails?.data?.details?.kyc &&
      authDetails?.data?.details?.kyc?.status === 'approved'
    )
  }, [authDetails])

  // Check wallet balance
  const hasWalletBalance = useMemo(() => {
    return accounts
      .filter((account: { type?: string }) => account.type === 'fiat')
      .some(
        (account: { availableBalance?: number }) =>
          (account.availableBalance || 0) > 0,
      )
  }, [accounts])

  // Check transaction history
  const hasTransactions = useMemo(() => {
    const transactions = accounts?.filter(
      (account: { type?: string }) => account.type === 'project',
    )
    return transactions
  }, [accounts])
  // TODO: comment out later
  // const hasTransactions = useMemo(() => {
  //   const transactions = accounts
  //     ?.filter(
  //       (account: { type?: string }) =>
  //         account.type !== 'fiat' && account.type !== 'asset',
  //     )
  //     ?.some(
  //       (account: { availableBalance?: number }) =>
  //         (account.availableBalance || 0) > 0,
  //     )
  //   return transactions
  // }, [accounts])

  // Determine current step
  let currentStep = 1 // KYC
  if (hasKyc) {
    currentStep = 2 // Charge Wallet
    if (hasWalletBalance) {
      currentStep = 3 // Start Transaction
    }
  }

  // Get step configuration
  const phone = authDetails?.data?.phone
  const email = authDetails?.data?.email

  const stepConfig = useMemo(() => {
    const configs = {
      1: {
        title: t('common_kyc_alert', { company_name: t('company_name') }),
        buttonText: t('common_kyc_title'),
        href: phone
          ? `/auth/quick-kyc?mobileOrEmail=${phone}`
          : `/auth/kyc?mobileOrEmail=${email}`,
      },
      2: {
        title: t('walletProgressAlert_title'),
        buttonText: t('walletProgressAlert_button_charge'),
        href: '/wallet/deposit/',
      },
      3: {
        title: t('walletProgressAlert_title_transaction'),
        buttonText: t('walletProgressAlert_button_transaction'),
        href: '/quick-trade',
      },
    }
    return configs[currentStep as keyof typeof configs] || configs[1]
  }, [currentStep, phone, email, t])

  // Don't show if all steps are completed
  if (hasKyc && hasWalletBalance && hasTransactions) {
    return null
  }

  // Don't show while loading
  if (isPendingAuth || isPendingPortfolio || isPendingTransactions) {
    return null
  }

  const title = () => {
    return (
      <Col className='gap-xs items-center flex-col sm:flex-row justify-center sm:justify-start'>
        <Image src='/images/gard.webp' alt='KYC Alert' width={48} height={48} />

        {/* Title Text */}
        <Typography
          size={isMobile ? 'sm' : 'md'}
          weight='regular'
          className={cn(
            'text-center sm:text-right w-full',
            isMobile ? 'text-alert-surface-100' : 'text-alert-surface-100',
          )}
        >
          {stepConfig.title}
        </Typography>
      </Col>
    )
  }

  const getStepNumber = (step: number) => {
    if (language === 'fa') {
      const persianNumbers = ['۱', '۲', '۳']
      return persianNumbers[step - 1] || step.toString()
    }
    return step.toString()
  }

  const steps = () => {
    return (
      <Col className='gap-2xs w-full my-xs'>
        {/* Step 1: KYC */}
        <Row className='gap-sm items-start'>
          <Col className='gap-2xs w-fit items-center'>
            {currentStep > 1 ? (
              <div className='w-5 h-5 p-2xs rounded-full flex items-center justify-center'>
                <Icon
                  name='CheckLine'
                  color='fill-positive-surface-100'
                  className='w-full h-full'
                />
              </div>
            ) : (
              <div className='w-5 h-5 rounded-full bg-alert-main flex items-center justify-center'>
                <Typography
                  size='xs'
                  weight='bold'
                  className='text-alert-main-100 pt-1'
                >
                  {getStepNumber(1)}
                </Typography>
              </div>
            )}

            <div className='w-0.5 h-4 border-l ps-[4px] border-l border-base-surface-dark' />
          </Col>
          <Typography
            size={isMobile ? 'sm' : 'md'}
            weight='bold'
            className={cn(
              'whitespace-nowrap',
              currentStep > 1
                ? 'text-positive-surface-100'
                : 'text-neutral-surface-100',
            )}
          >
            {t('walletProgressAlert_step_kyc')}
          </Typography>
        </Row>

        {/* Step 2: Charge Wallet */}
        <Row className='gap-sm items-start'>
          <Col className='gap-2xs w-fit items-center'>
            {currentStep > 2 ? (
              <div className='w-5 h-5 p-2xs rounded-full flex items-center justify-center'>
                <Icon
                  name='CheckLine'
                  color='fill-positive-surface-100'
                  className='w-full h-full'
                />
              </div>
            ) : (
              <div className='w-5 h-5 rounded-full bg-alert-main flex items-center justify-center'>
                <Typography
                  size='xs'
                  weight='bold'
                  className='text-alert-main-100 pt-1'
                >
                  {getStepNumber(2)}
                </Typography>
              </div>
            )}

            <div className='w-0.5 h-4 border-l ps-[4px] border-l border-base-surface-dark' />
          </Col>
          <Typography
            size={isMobile ? 'sm' : 'md'}
            weight='bold'
            className={cn(
              'whitespace-nowrap',
              currentStep > 2
                ? 'text-positive-surface-100'
                : 'text-neutral-surface-100',
            )}
          >
            {t('walletProgressAlert_step_charge')}
          </Typography>
        </Row>

        {/* Step 3: Start Transaction */}
        <Row className='gap-sm items-start'>
          <Col className='gap-2xs w-fit items-center'>
            {hasTransactions ? (
              <div className='w-5 h-5 p-2xs rounded-full flex items-center justify-center'>
                <Icon
                  name='CheckLine'
                  color='fill-positive-surface-100'
                  className='w-full h-full'
                />
              </div>
            ) : (
              <div className='w-5 h-5 rounded-full bg-alert-main flex items-center justify-center'>
                <Typography
                  size='xs'
                  weight='bold'
                  className='text-alert-main-100 pt-1'
                >
                  {getStepNumber(3)}
                </Typography>
              </div>
            )}
          </Col>
          <Typography
            size={isMobile ? 'sm' : 'md'}
            weight='bold'
            className={cn(
              'whitespace-nowrap',
              hasTransactions
                ? 'text-positive-surface-100'
                : 'text-neutral-surface-100',
            )}
          >
            {t('walletProgressAlert_step_transaction')}
          </Typography>
        </Row>
      </Col>
    )
  }

  const desktopSteps = () => {
    return (
      <Row className='gap-md items-center'>
        {/* Step 1: KYC */}
        <Row className='gap-xs items-center'>
          {currentStep > 1 ? (
            <div className='w-5 h-5 p-2xs rounded-full flex items-center justify-center'>
              <Icon
                name='CheckLine'
                color='fill-positive-surface-100'
                className='w-full h-full'
              />
            </div>
          ) : (
            <div className='w-5 h-5 rounded-full bg-alert-main flex items-center justify-center'>
              <Typography
                size='xs'
                weight='bold'
                className='text-alert-main-100 pt-1'
              >
                {getStepNumber(1)}
              </Typography>
            </div>
          )}
          <Typography
            size='md'
            weight='bold'
            className={cn(
              'whitespace-nowrap',
              currentStep > 1
                ? 'text-positive-surface-100'
                : 'text-neutral-surface-100',
            )}
          >
            {t('walletProgressAlert_step_kyc')}
          </Typography>
          <div className='h-0.5 w-10 ms-2 border-t border-base-surface-dark' />
        </Row>

        {/* Step 2: Charge Wallet */}
        <Row className='gap-xs items-center'>
          {currentStep > 2 ? (
            <div className='w-5 h-5 p-2xs rounded-full flex items-center justify-center'>
              <Icon
                name='CheckLine'
                color='fill-positive-surface-100'
                className='w-full h-full'
              />
            </div>
          ) : (
            <div className='w-5 h-5 rounded-full bg-alert-main flex items-center justify-center'>
              <Typography
                size='xs'
                weight='bold'
                className='text-alert-main-100 pt-1'
              >
                {getStepNumber(2)}
              </Typography>
            </div>
          )}
          <Typography
            size='md'
            weight='bold'
            className={cn(
              'whitespace-nowrap',
              currentStep > 2
                ? 'text-positive-surface-100'
                : 'text-neutral-surface-100',
            )}
          >
            {t('walletProgressAlert_step_charge')}
          </Typography>
          <div className='h-0.5 w-10 ms-2 border-t border-base-surface-dark' />
        </Row>

        {/* Step 3: Start Transaction */}
        <Row className='gap-xs items-center'>
          {hasTransactions ? (
            <div className='w-5 h-5 p-2xs rounded-full flex items-center justify-center'>
              <Icon
                name='CheckLine'
                color='fill-positive-surface-100'
                className='w-full h-full'
              />
            </div>
          ) : (
            <div className='w-5 h-5 rounded-full bg-alert-main flex items-center justify-center'>
              <Typography
                size='xs'
                weight='bold'
                className='text-alert-main-100 pt-1'
              >
                {getStepNumber(3)}
              </Typography>
            </div>
          )}
          <Typography
            size='md'
            weight='bold'
            className={cn(
              'whitespace-nowrap',
              hasTransactions
                ? 'text-positive-surface-100'
                : 'text-neutral-surface-100',
            )}
          >
            {t('walletProgressAlert_step_transaction')}
          </Typography>
        </Row>
      </Row>
    )
  }

  const actionButton = () => {
    return (
      <ButtonLink
        variant='fill'
        size='md'
        color='alert'
        buttonClassName={cn(
          'px-sm',
          isMobile
            ? 'w-full sm:max-w-[422px] bg-gradient-to-r from-alert-main-light to-alert-main text-alert-main-100 hover:opacity-90'
            : 'bg-gradient-to-r from-alert-main-light to-alert-main text-alert-main-100 hover:opacity-90',
        )}
        className={isMobile ? 'w-full sm:max-w-[422px]' : ''}
        href={stepConfig.href}
      >
        {stepConfig.buttonText}
      </ButtonLink>
    )
  }

  if (authDetails?.data?.details?.kyc?.status === 'waiting_for_review') {
    return <KycAlert data={authDetails?.data} />
  }

  // on desktop
  if (!isMobile) {
    return (
      <Row className={className}>
        <Card
          className='w-full border border-alert-main rounded-sm p-md'
          withoutBorder
        >
          <Row className='gap-4xl items-center w-full'>
            <Col className='gap-2xs items-center'>
              {/* Shield Icon with Warning */}
              {title()}
            </Col>
            {/* Progress Steps - Horizontal */}
            {desktopSteps()}
            {/* Action Button */}
            <div className='flex-shrink-0'>{actionButton()}</div>
          </Row>
        </Card>
      </Row>
    )
  }

  return (
    <Row className={className}>
      <Card
        className='w-full border border-alert-main rounded-sm p-md'
        withoutBorder
      >
        <Col className='gap-md items-center'>
          {/* Shield Icon with Warning */}
          {title()}

          {/* Progress Steps */}
          {steps()}

          {/* Action Button */}
          {actionButton()}
        </Col>
      </Card>
    </Row>
  )
}
